import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppData } from '../store/dataStore';
import { FaRobot, FaImage, FaCloudUploadAlt } from 'react-icons/fa';
import { Box } from '@chakra-ui/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  HeroWithEnrollmentCard,
  CurriculumSection,
  ProjectsSection,
  TestimonialsSection,
  FAQSection
} from '../components/enrollnow_components';
import { Helmet } from 'react-helmet-async';
import trackingService from '../services/trackingService';
import { useAffiliateCode } from '../hooks/useAffiliateCode';

const EnrollNowPage = () => {
  const [searchParams] = useSearchParams();
  const rawCourseParam = searchParams.get('courseId');
  const { appData } = useAppData();

  // Initialize affiliate code tracking
  useAffiliateCode('enrollment_page_visit');

  // Parse courseId parameter in format: "4-cou-flg" (courseId-influencerId-flags)
  let courseId = '1';
  let influencerId = '';
  let flags = '';

  if (rawCourseParam) {
    const parts = rawCourseParam.split('-');
    courseId = parts[0] || '1';
    influencerId = parts[1] || '';
    flags = parts[2] || '';
  }

  // Set influencer if provided
  useEffect(() => {
    if (influencerId) {
      trackingService.setInfluencer(influencerId);
      trackingService.track('enrollment_visit', {
        courseId,
        influencerId,
        flags
      });
    }
  }, [influencerId, courseId, flags]);

  const { course1, course2, course3, course4, course11, studentsCount, course6 } = appData;

  // Determine which course based on courseId
  let course;
  if (courseId === "2") {
    course = course2;
  } else if (courseId === "3") {
    course = course3;
  } else if (courseId === "4") {
    course = course4;
  } else if (courseId === "6") {
    course = course6;
  } else if (courseId === "11") {
    course = course11;
  } else {
    course = course1; // Default to course1
  }

  // Construct page data with actual values from store
  const pageData = {
    hero: {
      badge: `Next Batch: Starts ${course.startDate}`,
      title: "Build Production GenAI Apps — Full Stack Included",
      description: "Hands-on GenAI + Full Stack program. Ship deployable AI services, build a portfolio of production apps, and get interview-ready with mentor-led reviews.",
      buttons: [
        {
          text: "View Curriculum",
          variant: "outline",
          size: "md",
          h: "50px",
          px: 6,
          href: course.link
        }
      ],
      stats: [
        { icon: 'FaUsers', text: `${studentsCount}+ Learners` },
        { icon: 'FaClock', text: "Flex: Evenings & Weekends" }
      ]
    },

    enrollmentCard: {
      track: course.track,
      shortName: course.shortName,
      courseId: courseId || '1',
      popularTag: "New Arrival",
      price: `₹${course.price}`,
      priceNote: "One-time",
      description: course.offering,
      form: {
        fields: [
          { label: "Full name", name: "name", type: "text", required: true, defaultValue: '' },
          { label: "Email", name: "email", type: "email", required: true, defaultValue: '' },
          { label: "Phone", name: "phone", type: "tel", required: true, defaultValue: '' },
          { label: "Preferred Batch", name: "batch", type: "text", required: false, defaultValue: course.startDate },
          {
            label: "Apply for Scholarship",
            name: "scholarshipOpt",
            type: "checkbox",
            required: false,
            defaultValue: false,
            helperText: "Scholarships are available based on merit, inclusion, and gender diversity. Subject to approval.",
            confirmMessage: "Your application is subject to approval. By selecting this, you grant permission for our sponsor to contact you and gather necessary information for the evaluation process."
          }
        ],
        submitText: "Complete My Enrollment"
      }
    },

    curriculum: {
      title: "What you'll learn",
      description: "A practical, project-first curriculum covering GenAI fundamentals, vector search, RAG pipelines, and full-stack deployment.",
      cards: [
        {
          icon: 'FaGraduationCap',
          title: "ML & LLM Foundations",
          description: "ML refresher, transformer internals, prompt engineering, evaluation metrics."
        },
        {
          icon: 'FaCheckCircle',
          title: "Embeddings & Vector DBs",
          description: "Embedding strategies, similarity search, Pinecone/Weaviate, and indexing at scale."
        },
        {
          icon: 'FaWallet',
          title: "Full Stack & Deployment",
          description: "API design, containerization, CI/CD, serverless functions, and monitoring in production."
        },
        {
          icon: 'FaStar',
          title: "Projects & Portfolio",
          description: "Five production-grade projects with GitHub repos, live demos, and deployment guides."
        },
        {
          icon: 'FaUsers',
          title: "Career Support",
          description: "Resume reviews, mock interviews, placement assistance, and hiring-ready projects."
        },
        {
          icon: 'FaClock',
          title: "Flexible Schedule",
          description: "Evening batches, weekend intensives, and recorded sessions for review."
        }
      ]
    },

  projects: {
  title: "Projects you'll finish",
  description: "Each project is designed to go into your portfolio with deployment and README.",
  items: [
    {
      icon: FaRobot,
      title: "RAG Chatbot",
      description: "Contextual assistant with vector search and multi-document support."
    },
    {
      icon: FaImage,
      title: "Multi-modal App",
      description: "Image + text pipeline with retrieval and generation."
    },
    {
      icon: FaCloudUploadAlt,
      title: "Deployed API",
      description: "Secure API with CI/CD, monitoring and autoscaling patterns."
    }
  ]
},
    testimonials: {
      title: "Proven outcomes",
      description: "Graduates placed in product teams and startups. Real salary uplifts and promotions.",
      stats: [
        { value: "85%", label: "Interview ready" },
        { value: "3x", label: "Project to interview ratio" }
      ],
      testimonials: [
        {
          name: "Rahul M",
          role: "AI Developer",
          text: "\"The course pushed me to build deployable systems. Mentor code reviews were the difference.\""
        },
        {
          name: "Yashwant",
          role: "AI Developer",
          text: "\"The project clarity and deployment tasks built my confidence for internal interviews.\""
        }
      ]
    },

    faq: {
      title: "Frequently asked questions",
      items: [
        {
          question: "Do I need prior ML experience?",
          answer: "No. We cover ML fundamentals and transformer internals. The course ramps from core concepts to production patterns."
        },
        {
          question: "Can I attend while working full-time?",
          answer: "Yes. Evening and weekend batches are scheduled. All sessions are recorded for revision."
        },
        {
          question: "What is the refund policy?",
          answer: "14-day refund from first live session. Terms in enrollment agreement."
        }
      ]
    }
  };

  const handleEnrollSubmit = (e) => {
    e.preventDefault();
    console.log('Enroll form submitted');
  };

  return (
    <>
      <Helmet>
        <title>Enroll in GenAI Training | Secure Your Spot in the Next Batch</title>
        <meta name="description" content="Join the next batch of GenAI training. Build deployable AI apps, gain mentor support, and access lifetime community benefits. 14-day risk-free guarantee." />
      </Helmet>

      <Box>
        <Navbar />

        <HeroWithEnrollmentCard
          heroData={pageData.hero}
          enrollmentData={pageData.enrollmentCard}
          onSubmit={handleEnrollSubmit}
        />
        <CurriculumSection data={pageData.curriculum} />
        <ProjectsSection data={pageData.projects} />
        <TestimonialsSection data={pageData.testimonials} />
        <FAQSection data={pageData.faq} />

        <Footer />
      </Box>
    </>
  );
};

export default EnrollNowPage;
